import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { config } from '../config';
import { birthdayPartyBookingQuestions } from './birthdayPartyBookingQuestions';
import type { SurveyQuestion } from './types';
import './survey.css';

const SURVEY_SLUG = 'birthday-party-booking';

// Section groupings for visual layout
const SECTIONS = [
  {
    title: '1. Your Contact Information',
    ids: ['q_name', 'q_email', 'q_phone', 'q_contact_pref'],
  },
  {
    title: '2. Birthday Child',
    ids: ['q_child_name', 'q_child_age'],
  },
  {
    title: '3. Party Date & Time',
    ids: ['q_party_date', 'q_party_time', 'q_alt_date'],
  },
  {
    title: '4. Group Size',
    ids: ['q_num_kids'],
  },
  {
    title: '5. LEGO Robotics Theme',
    ids: ['q_theme'],
  },
  {
    title: '6. Additional Information',
    ids: ['q_special_requests', 'q_heard_from'],
  },
  {
    title: '7. Cancellation Policy',
    ids: ['q_agree_cancellation'],
  },
];

const QuestionRenderer: React.FC<{
  question: SurveyQuestion;
  value: string | string[] | number | undefined;
  onChange: (value: string | string[] | number) => void;
}> = ({ question, value, onChange }) => {
  const id = `bpb-q-${question.id}`;

  switch (question.type) {
    case 'text':
    case 'email':
      return (
        <input
          id={id}
          type={question.type}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="survey-input"
        />
      );
    case 'textarea':
      return (
        <textarea
          id={id}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="survey-textarea"
          rows={4}
        />
      );
    case 'radio':
      return (
        <div className="survey-options" role="radiogroup">
          {(question.options ?? []).map((opt) => (
            <label key={opt.value} className="survey-option">
              <input
                type="radio"
                name={question.id}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      );
    default:
      return (
        <input
          id={id}
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="survey-input"
        />
      );
  }
};

const BirthdayPartyBooking: React.FC = () => {
  const [responses, setResponses] = useState<Record<string, string | string[] | number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setAnswer = useCallback((questionId: string, value: string | string[] | number) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      const hasRequired = birthdayPartyBookingQuestions.every((q) => {
        if (!q.required) return true;
        const v = responses[q.id];
        if (Array.isArray(v)) return v.length > 0;
        return v !== undefined && String(v).trim() !== '';
      });
      if (!hasRequired) {
        setSubmitError('Please answer all required questions (marked with *) before submitting.');
        return;
      }

      setSubmitting(true);
      try {
        const payload = {
          survey_slug: SURVEY_SLUG,
          responses: Object.entries(responses).map(([question_id, value]) => ({
            question_id,
            value,
          })),
        };
        await axios.post(`${config.BACKEND_URL}/api/surveys/submit`, payload);
        window.location.href = 'https://oarayouthinnovationlab.ca/';
      } catch (err: any) {
        console.error('Booking submission error:', err);
        setSubmitError(
          err?.response?.data?.detail ||
          'There was a problem submitting your request. Please try again or email us directly.'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [responses]
  );

  return (
    <div className="survey-page bpb-page">
      <div className="survey-card bpb-card">

        {/* Header */}
        <header className="bpb-header">
          <div className="bpb-org-label">OARA Youth Innovation Lab · Vernon, BC</div>
          <h1>🎉 Birthday Party Booking Request</h1>
          <div className="bpb-intro-box">
            <p>
              Fill out the form below to request your LEGO robotics birthday party. We will confirm
              availability and send you payment details within 1–2 business days.
            </p>
            <div className="bpb-includes">
              <span>🤖 1 hr guided robotics</span>
              <span>🎂 1 hr party time</span>
              <span>🏓 Foosball & ping pong</span>
              <span>🪑 Tables & chairs included</span>
            </div>
          </div>
        </header>

        {/* Sectioned form */}
        <form onSubmit={handleSubmit} className="survey-form">
          {SECTIONS.map((section) => {
            const sectionQuestions = birthdayPartyBookingQuestions.filter((q) =>
              section.ids.includes(q.id)
            );
            if (sectionQuestions.length === 0) return null;
            return (
              <div key={section.title} className="bpb-section">
                <div className="bpb-section-title">{section.title}</div>
                {sectionQuestions.map((q) => (
                  <div key={q.id} className="survey-question">
                    <label
                      htmlFor={`bpb-q-${q.id}`}
                      className="survey-question-label"
                    >
                      {q.label}
                      {q.required && <span className="survey-required"> *</span>}
                    </label>
                    <QuestionRenderer
                      question={q}
                      value={responses[q.id]}
                      onChange={(v) => setAnswer(q.id, v)}
                    />
                  </div>
                ))}
              </div>
            );
          })}

          {/* Price summary reminder */}
          <div className="bpb-price-reminder">
            <strong>Package total: $175</strong> — payment is collected after we confirm your date.
          </div>

          {submitError && (
            <div className="survey-submit-error">{submitError}</div>
          )}

          <div className="survey-actions">
            <button type="submit" className="bpb-submit-btn" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Booking Request 🎉'}
            </button>
            <p className="bpb-submit-note">We'll reply within 1–2 business days to confirm your date.</p>
          </div>
        </form>

        {/* Footer contact */}
        <div className="bpb-footer-contact">
          <strong>Questions?</strong>{' '}
          <a href="mailto:oarayouthinnovationlab@gmail.com">oarayouthinnovationlab@gmail.com</a>
          {' '}· 236 795 0470 (text only) · 3125, 31 Ave, Vernon, BC
        </div>
      </div>
    </div>
  );
};

export default BirthdayPartyBooking;
